import { Link, useLocation } from 'react-router-dom'
import {
  Activity,
  Shield,
  Users,
  Puzzle,
  DollarSign,
  LogOut,
  Menu,
  Command,
  ChevronRight
} from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Sheet, SheetContent, SheetTrigger } from '@/components/ui/sheet'
import { useAuthStore } from '@/stores/auth-store'
import { ThemeSwitcher } from '@/components/theme-switcher'
import { useIsMobile } from '@/hooks/use-mobile'
import { cn } from '@/lib/utils'

interface SidebarProps {
  className?: string
}

export function Sidebar({ }: SidebarProps) {
  const location = useLocation()
  const { staff, logout } = useAuthStore()
  const isMobile = useIsMobile()

  const navigation = [
    { name: 'Overview', href: '/dashboard', icon: Activity },
    { name: 'Licenses', href: '/dashboard/licenses', icon: Shield },
    { name: 'Tenants', href: '/dashboard/tenants', icon: Users },
    { name: 'Extensions', href: '/dashboard/extensions', icon: Puzzle },
    { name: 'Billing', href: '/dashboard/billing', icon: DollarSign },
  ]

  const NavContent = () => (
    <div className="flex h-full flex-col bg-slate-50/50 dark:bg-slate-900/50 backdrop-blur-xl">
      {/* Header */}
      <div className="flex h-16 items-center px-6 border-b border-slate-200/60 dark:border-slate-800/60">
        <Link to="/dashboard" className="flex items-center gap-3 font-semibold group w-full">
          <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-slate-900 text-white dark:bg-white dark:text-slate-900 shadow-sm transition-transform group-hover:scale-105">
            <Command className="h-4 w-4" />
          </div>
          <div className="flex flex-col gap-0.5">
            <span className="text-sm font-bold tracking-tight text-slate-900 dark:text-slate-100">MaldevtaHub</span>
            <span className="text-[10px] text-slate-500 font-medium uppercase tracking-wider">Admin Console</span>
          </div>
        </Link>
      </div>

      {/* Nav Items */}
      <div className="flex-1 overflow-y-auto py-6 px-3">
        <nav className="flex flex-col gap-1">
          <div className="px-3 mb-2">
            <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">Main Menu</p>
          </div>
          {navigation.map((item) => {
            const Icon = item.icon
            const isActive = location.pathname === item.href ||
              (item.href !== '/dashboard' && location.pathname.startsWith(item.href))

            return (
              <Link
                key={item.name}
                to={item.href}
                className={cn(
                  "group flex items-center justify-between rounded-md px-3 py-2 text-sm font-medium transition-all duration-200 ease-in-out",
                  isActive
                    ? "bg-white dark:bg-slate-800 text-slate-900 dark:text-white shadow-sm border border-slate-200/60 dark:border-slate-700"
                    : "text-slate-500 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800 hover:text-slate-900 dark:hover:text-slate-200"
                )}
              >
                <div className="flex items-center gap-3">
                  <Icon className={cn(
                    "h-4 w-4 transition-colors",
                    isActive ? "text-primary" : "text-slate-400 group-hover:text-slate-600 dark:text-slate-500 dark:group-hover:text-slate-300"
                  )} />
                  {item.name}
                </div>
                {isActive && (
                  <ChevronRight className="h-3 w-3 text-slate-400 animate-in slide-in-from-left-1" />
                )}
              </Link>
            )
          })}
        </nav>
      </div>

      {/* Footer */}
      <div className="border-t border-slate-200/60 dark:border-slate-800/60 p-4 bg-white/50 dark:bg-slate-900/50">
        <div className="flex items-center justify-between mb-2">
           <span className="text-[10px] font-bold text-slate-400 uppercase tracking-widest px-2">Account</span>
           <ThemeSwitcher />
        </div>
        
        <div className="flex items-center gap-3 p-2 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors cursor-pointer group">
          <div className="h-8 w-8 rounded-full bg-slate-900 dark:bg-slate-100 flex items-center justify-center text-white dark:text-slate-900 shrink-0">
            <span className="text-xs font-bold">{staff?.name?.charAt(0) || 'A'}</span>
          </div>
          <div className="flex flex-col flex-1 min-w-0">
            <span className="text-xs font-semibold text-slate-900 dark:text-slate-100 truncate group-hover:text-black dark:group-hover:text-white transition-colors">{staff?.name}</span>
            <span className="text-[10px] text-slate-500 truncate capitalize">{staff?.role}</span>
          </div>
          <Button 
            variant="ghost" 
            size="icon" 
            className="h-7 w-7 text-slate-400 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-950/30 opacity-0 group-hover:opacity-100 transition-opacity"
            onClick={(e) => {
              e.stopPropagation()
              logout()
            }}
            title="Sign out"
          >
            <LogOut className="h-3.5 w-3.5" />
          </Button>
        </div>
      </div>
    </div>
  )

  if (isMobile) {
    return (
      <Sheet>
        <SheetTrigger asChild>
          <Button variant="outline" size="icon" className="md:hidden fixed top-4 left-4 z-50 h-9 w-9 bg-background/80 backdrop-blur-sm border-slate-200 dark:border-slate-800">
            <Menu className="h-4 w-4" />
            <span className="sr-only">Toggle menu</span>
          </Button>
        </SheetTrigger>
        <SheetContent side="left" className="p-0 w-72 border-r border-slate-200 dark:border-slate-800">
          <NavContent />
        </SheetContent>
      </Sheet>
    )
  }

  return (
    <aside className="hidden md:flex h-screen w-64 flex-col fixed inset-y-0 left-0 z-30 border-r border-slate-200 dark:border-slate-800 shadow-sm">
      <NavContent />
    </aside>
  )
}

